import {
  categoryForMetric,
  EventLogMetricCategory,
  EXCLUDED_METRICS,
} from '@chiroup/core/constants/metricCategories';
import React, { useMemo } from 'react';
import MetricCard from './MetricCard';

type Props = {
  category: EventLogMetricCategory;
  metrics: Record<string, number>;
  previousMetrics: Record<string, number>;
  dailyByMetric: Record<string, number[]>;
  onMetricClick: (metricKey: string) => void;
  onMetricExpand: (metricKey: string) => void;
  loading?: boolean;
};

const SkeletonCard: React.FC = () => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded skeleton-shimmer" />
        <div className="h-6 w-14 rounded skeleton-shimmer" />
      </div>
      <div className="h-4 w-10 rounded skeleton-shimmer" />
    </div>
    <div className="h-3 w-28 rounded skeleton-shimmer mt-1" />
    <div className="h-6 w-full rounded skeleton-shimmer mt-3" />
  </div>
);

const MetricGrid: React.FC<Props> = ({
  category,
  metrics,
  previousMetrics,
  dailyByMetric,
  onMetricClick,
  onMetricExpand,
  loading,
}) => {
  // All metrics in this category (union of current & previous keys, plus any metric
  // we have a category mapping for that landed here).
  const items = useMemo(() => {
    const keys = new Set<string>();

    for (const k of Object.keys(metrics)) {
      if (!EXCLUDED_METRICS.has(k) && categoryForMetric(k) === category) {
        keys.add(k);
      }
    }

    for (const k of Object.keys(previousMetrics)) {
      if (!EXCLUDED_METRICS.has(k) && categoryForMetric(k) === category) {
        keys.add(k);
      }
    }

    return Array.from(keys)
      .map(k => ({
        metricKey: k,
        value: metrics[k] ?? 0,
        previousValue: previousMetrics[k] ?? 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [category, metrics, previousMetrics]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-12 text-center">
        <p className="text-sm text-gray-500">No activity in this category for the selected range</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map(item => (
        <MetricCard
          key={item.metricKey}
          metricKey={item.metricKey}
          value={item.value}
          previousValue={item.previousValue}
          series={dailyByMetric[item.metricKey]}
          onClick={() => onMetricClick(item.metricKey)}
          onExpand={() => onMetricExpand(item.metricKey)}
        />
      ))}
    </div>
  );
};

export default MetricGrid;
